import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Badge, Box, Button, Divider, Drawer, IconButton, Stack, Typography,
} from '@mui/material';
import NotificationsRoundedIcon from '@mui/icons-material/NotificationsRounded';
import DoneAllRoundedIcon from '@mui/icons-material/DoneAllRounded';
import { useNavigate } from 'react-router-dom';
import { apiFetch } from '../utils/apiFetch';
import { getApiUrl } from '../utils/api';

const API_URL = getApiUrl();

export default function NotificationBell({ enabled }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [error, setError] = useState('');
  const activeRef = useRef(false);
  const loadGenerationRef = useRef(0);
  const listGenerationRef = useRef(0);

  const load = useCallback(async (full = false) => {
    if (!enabled) return;
    const generation = ++loadGenerationRef.current;
    const listGeneration = full ? ++listGenerationRef.current : null;
    try {
      const response = await apiFetch(`${API_URL}/api/notifications${full ? '' : '/unread-count'}`);
      if (!response.ok) throw new Error('Falha ao consultar notificações.');
      const data = await response.json();
      if (!activeRef.current) return;
      if (generation === loadGenerationRef.current) {
        setUnread(Number(data.unreadCount || 0));
        setError('');
      }
      if (full && listGeneration === listGenerationRef.current && Array.isArray(data.notifications)) {
        setItems(data.notifications);
      }
    } catch (_) {
      // Keep the last confirmed count/list; the next poll, focus or drawer open retries.
      if (activeRef.current && generation === loadGenerationRef.current) {
        setError('Não foi possível atualizar as notificações.');
      }
    }
  }, [enabled]);

  useEffect(() => {
    if (!enabled) return undefined;
    activeRef.current = true;
    load(false);
    const timer = window.setInterval(() => load(false), 60000);
    const onFocus = () => load(false);
    window.addEventListener('focus', onFocus);
    return () => {
      activeRef.current = false;
      loadGenerationRef.current += 1;
      listGenerationRef.current += 1;
      window.clearInterval(timer);
      window.removeEventListener('focus', onFocus);
    };
  }, [enabled, load]);

  if (!enabled) return null;

  const show = async () => { setOpen(true); await load(true); };
  const markAll = async () => {
    try {
      const response = await apiFetch(`${API_URL}/api/notifications/read-all`, { method: 'PATCH' });
      if (!response.ok) throw new Error('Falha ao marcar notificações como lidas.');
      if (!activeRef.current) return;
      loadGenerationRef.current += 1;
      listGenerationRef.current += 1;
      setUnread(0);
      setItems((current) => current.map((item) => ({ ...item, read: true })));
      setError('');
    } catch (_) {
      if (activeRef.current) setError('Não foi possível marcar as notificações como lidas.');
    }
  };
  const select = async (item) => {
    if (!item.read) {
      try {
        const response = await apiFetch(`${API_URL}/api/notifications/${item.id}/read`, { method: 'PATCH' });
        if (!response.ok) throw new Error('Falha ao marcar notificação como lida.');
        if (!activeRef.current) return;
        loadGenerationRef.current += 1;
        listGenerationRef.current += 1;
        setUnread((count) => Math.max(0, count - 1));
        setItems((current) => current.map((entry) => entry.id === item.id ? { ...entry, read: true } : entry));
        setError('');
      } catch (_) {
        if (activeRef.current) setError('Não foi possível marcar a notificação como lida.');
        return;
      }
    }
    if (!activeRef.current) return;
    if (item.actionUrl?.startsWith('/') && !item.actionUrl.startsWith('//')) navigate(item.actionUrl);
    setOpen(false);
  };

  return (
    <>
      <IconButton
        aria-label={`Notificações${unread ? `, ${unread} não lidas` : ''}`}
        onClick={show}
        sx={{ position: 'fixed', top: 10, right: 14, zIndex: 1250, bgcolor: 'background.paper', boxShadow: 2 }}
      >
        <Badge color="error" badgeContent={unread} max={99}><NotificationsRoundedIcon /></Badge>
      </IconButton>
      <Drawer anchor="right" open={open} onClose={() => setOpen(false)}>
        <Box sx={{ width: { xs: 'min(92vw, 380px)', sm: 400 }, p: 2 }} role="dialog" aria-label="Notificações">
          <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
            <Typography variant="h6">Notificações</Typography>
            <Button size="small" startIcon={<DoneAllRoundedIcon />} disabled={!unread} onClick={markAll}>Ler todas</Button>
          </Stack>
          <Divider />
          {error ? <Typography role="alert" color="error" sx={{ py: 2 }}>{error}</Typography> : null}
          {!items.length ? <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>Nenhuma notificação.</Typography> : items.map((item) => (
            <Button key={item.id} fullWidth onClick={() => select(item)} sx={{ display: 'block', textAlign: 'left', p: 2, borderBottom: 1, borderColor: 'divider', borderRadius: 0, bgcolor: item.read ? 'transparent' : 'action.hover' }}>
              <Typography variant="subtitle2" color="text.primary">{item.title}</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'normal', mt: 0.5 }}>{item.body}</Typography>
              <Typography variant="caption" color="text.disabled">{new Date(item.publishedAt).toLocaleString('pt-BR')}</Typography>
            </Button>
          ))}
        </Box>
      </Drawer>
    </>
  );
}
